import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  Alert,
} from '@mui/material';
import { Description as DescriptionIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchDailyOrderReports, fetchSalesSummaryReport, fetchFinancialSettlementReport, fetchAllDayChecks, clearReports } from '../store/slices/reportsSlice';
import { fetchAllBranches } from '../store/slices/branchSlice';
import { ReportType } from '../types';
import ReportFilters from '../components/Reports/ReportFilters';
import SalesSummaryReport from '../components/Reports/SalesSummaryReport';
import DailyOrdersReport from '../components/Reports/DailyOrdersReport';
import FinancialSettlementReport from '../components/Reports/FinancialSettlementReport';
import AllDayChecksReport from '../components/Reports/AllDayChecksReport';

const ReportsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  const [reportType, setReportType] = useState<ReportType>(ReportType.DAILY_ORDERS);
  
  const { dailyReports, salesSummaryReport, financialSettlementReport, allDayChecks, loading, error } = useAppSelector(state => state.reports);
  const branches = useAppSelector(state => state.branches.branches);
  
  useEffect(() => {
    // Fetch branches if not already loaded
    if (branches.length === 0) {
      dispatch(fetchAllBranches());
    }
  }, [dispatch, branches.length]);

  // No longer needed to clear reports on report type change - we'll do it in handleGenerateReport instead
  // This preserves filter state between report types without clearing the report unless a new one is generated



  const handleGenerateReport = () => {
    if (!startDate || !endDate) return;

    // Basic range validation
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start > end) return;

    // Send exact dates only (YYYY-MM-DD) with no time expansion
    const request = {
      dateFrom: format(start, 'yyyy-MM-dd'),
      dateTo: format(end, 'yyyy-MM-dd'),
      branchId: selectedBranchId || undefined,
    };

    // First clear any existing reports to prevent data from one report type showing in another
    dispatch(clearReports());

    // Dispatch the appropriate action based on report type
    if (reportType === ReportType.DAILY_ORDERS) {
      dispatch(fetchDailyOrderReports(request));
    } else if (reportType === ReportType.SALES_SUMMARY) {
      dispatch(fetchSalesSummaryReport(request));
    } else if (reportType === ReportType.FINANCIAL_SETTLEMENT) {
      dispatch(fetchFinancialSettlementReport(request));
    } else if (reportType === ReportType.ALL_DAY_CHECKS) {
      dispatch(fetchAllDayChecks(request));
    }
  };



  return (
    <Container maxWidth="xl" sx={{ p: 0 }}>
      <Box className="no-print">
        <ReportFilters
          startDate={startDate}
          endDate={endDate}
          selectedBranchId={selectedBranchId}
          selectedReportType={reportType}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onBranchChange={setSelectedBranchId}
          onReportTypeChange={(type) => {
            setReportType(type);
            if (type === ReportType.ALL_DAY_CHECKS && startDate) {
              setEndDate(startDate);
            }
          }}
          onGenerateReport={handleGenerateReport}
          loading={loading}
          branches={branches}
        />
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
        
      {/* Render the appropriate report component based on report type */}
      <Box className="print-area">
          {(() => {
            // Calculate selected branch name once
            const selectedBranchName = selectedBranchId
              ? (branches.find((b) => String(b.id) === String(selectedBranchId))?.name ?? String(selectedBranchId))
              : 'All Branches';

            switch (reportType) {
              case ReportType.SALES_SUMMARY:
                if (salesSummaryReport) {
                  return (
                    <SalesSummaryReport 
                      report={salesSummaryReport} 
                      selectedBranchName={selectedBranchName}
                    />
                  );
                }
                break;

              case ReportType.FINANCIAL_SETTLEMENT:
                if (financialSettlementReport) {
                  return (
                    <FinancialSettlementReport
                      report={financialSettlementReport}
                      selectedBranchName={selectedBranchName}
                    />
                  );
                }
                break;

              case ReportType.DAILY_ORDERS:
                return (
                  <DailyOrdersReport
                    dailyReports={dailyReports}
                    startDate={startDate}
                    endDate={endDate}
                    selectedBranchName={selectedBranchName}
                  />
                );

              case ReportType.ALL_DAY_CHECKS:
                return (
                  <AllDayChecksReport
                    checks={allDayChecks}
                    startDate={startDate}
                    endDate={endDate}
                    selectedBranchName={selectedBranchName}
                  />
                );

              default:
                break;
            }

            // Show no data message when reports are empty and not loading
            if (!loading) {
              return (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No data available. Please select a date range and generate a report.
                  </Typography>
                </Paper>
              );
            }

            return null;
          })()}
      </Box>
    </Container>
  );
};

export default ReportsPage;